import React, { ReactNode, useEffect } from 'react';

type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  width?: number;
  closeOnOverlayClick?: boolean;
  footer?: React.ReactNode; 
  children: ReactNode;
};

export default function Modal({
  open,
  onClose,
  title,
  closeOnOverlayClick = true,
  children,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;

    document.body.style.overflow = 'hidden';

    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onEsc);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onEsc);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={() => closeOnOverlayClick && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'flex-end',
      }}
    >
      {/* Bottom sheet */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          background: 'linear-gradient(145deg, rgba(15,23,42,0.98), rgba(2,6,23,0.98))',
          borderTopLeftRadius: 22,
          borderTopRightRadius: 22,
          border: '1px solid rgba(148,163,184,0.25)',
          boxShadow: '0 -20px 50px rgba(0,0,0,0.85)',
          padding: 16,
          color: '#e5e7eb',
          animation: 'slideUp 0.2s ease-out',
        }}
      >
        {/* Drag handle */}
        <div
          style={{
            width: 42,
            height: 5,
            borderRadius: 999,
            background: 'rgba(148,163,184,0.35)',
            margin: '0 auto 12px',
          }}
        />

        {/* Header */}
        {title && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 16 }}>{title}</div>

            <button
              onClick={onClose}
              style={{
                width: 34,
                height: 34,
                borderRadius: 999,
                border: '1px solid rgba(148,163,184,0.25)',
                background: 'rgba(255,255,255,0.06)',
                color: '#b1b8c7',
                fontSize: 16,
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Content */}
        <div>{children}</div>
      </div>

      {/* Animations */}
      <style>
        {`
          @keyframes slideUp {
            from { transform: translateY(30px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}
      </style>
    </div>
  );
}
